import React from "react";
import { apiPost } from "../utils/apiClient";

/**
 * プランアップグレード導線の共通コンポーネント。
 *
 * 「スタンダードプラン以上でご利用いただけます」ポップアップと、
 * アップグレード先リンク（Googleフォーム）をこの1ファイルで一元管理する。
 * リンク先を変えたいときは UPGRADE_FORM_URL だけを書き換えればよい。
 */

// アップグレード導線の遷移先（利益Navi 有料プラン申し込みフォーム）
// ※ PlanBillingPage の PLAN_CHANGE_FORM_URL と同じ正式URL（/viewform）で統一
export const UPGRADE_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSd5NyCMoqT5uH9Q_JTRaHZQk9x47HMvFPHkwgEH1MV9P-g5ZA/viewform";

// アップグレード導線クリックを運営の Chatwork へ通知する（結果は待たない・失敗しても無視）
export function notifyUpgradeInterest(feature?: string) {
    apiPost("/api/upgrade-interest", {
        feature: feature ?? "",
        page: typeof window !== "undefined" ? window.location.pathname : "",
    }).catch(() => {});
}

// アップグレードフォームを新しいタブで開く（アプリから離脱させない）
// ※ onClick={openUpgradeForm} と直接渡される箇所があるため、
//   第一引数が MouseEvent でも安全なように string のみ feature として扱う
export function openUpgradeForm(feature?: unknown) {
    notifyUpgradeInterest(typeof feature === "string" ? feature : undefined);
    window.open(UPGRADE_FORM_URL, "_blank", "noopener,noreferrer");
}

export type UpgradeFeature = { title: string; desc: string };

export default function FreePlanUpgradeDialog({
    open,
    onClose,
    featureName,
    features,
    description,
}: {
    open: boolean;
    onClose: () => void;
    /** 機能名。見出し「◯◯はスタンダードプラン以上でご利用いただけます」に使う */
    featureName: string;
    /** 機能の訴求カード（省略可） */
    features?: UpgradeFeature[];
    /** 補足説明（省略可） */
    description?: React.ReactNode;
}) {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4">
            <div className="w-full max-w-[480px] overflow-hidden rounded-xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                <div className="bg-[#4a72c4] px-7 pb-5 pt-6">
                    <div className="mb-2 text-[11px] font-medium tracking-[0.08em] text-[#cfdcf5]">
                        スタンダードプラン以上の機能
                    </div>
                    <div className="text-[17px] font-semibold leading-[1.5] text-white">
                        {featureName}はスタンダードプラン以上で
                        <br />
                        ご利用いただけます
                    </div>
                </div>

                <div className="px-7 py-6">
                    {features && features.length > 0 && (
                        <div className="mb-[18px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
                            {features.map((f, i) => (
                                <div key={i} className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                                    <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                        {f.title}
                                    </div>
                                    <div className="text-[12px] leading-[1.5] text-[#555]">
                                        {f.desc}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {description && (
                        <p className="mb-5 text-[13px] leading-[1.7] text-[#555]">{description}</p>
                    )}

                    <div className="flex gap-[10px] border-t border-[#eee] pt-4">
                        <button
                            className="flex-1 rounded-lg bg-[#4a72c4] px-4 py-[11px] text-[14px] font-semibold text-white transition hover:bg-[#3a5fad]"
                            onClick={() => openUpgradeForm(featureName)}
                        >
                            プランをアップグレードする →
                        </button>

                        <button
                            className="whitespace-nowrap rounded-lg border border-[#ddd] bg-white px-4 py-[11px] text-[13px] text-[#888] transition hover:bg-[#f5f5f5]"
                            onClick={onClose}
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
